import type {
  FeatureFlagValue,
  FeatureFlagsConfig,
} from "./featureFlages.types";

export type { FeatureFlagValue, FeatureFlagsConfig };

let _id: string | undefined = undefined;
const _flags: FeatureFlagsConfig = {};

/**
 * Set feature flag context
 * Called during bootstrap in main thread and via message in workers
 *
 * @param sessionId - Unique ID for consistent percentage rollout
 * @param featureFlags - Object with flag names and their configured values (boolean or percentage rollout)
 */
export function setContext(id: string, flags: FeatureFlagsConfig = {}): void {
  _id = id;
  Object.assign(_flags, flags);
}

/**
 * Set a single feature flag
 * @param flagName - Name of the feature flag
 * @param value - Flag value (boolean or percentage rollout)
 */
export function setFlag(flagName: string, value: FeatureFlagValue): void {
  _flags[flagName] = value;
}

export function clearContext(): void {
  _id = undefined;
  for (const key in _flags) {
    _flags[key] = undefined;
  }
}

/**
 * Get the current ID
 * @returns The current ID or undefined if not set.
 */
export function getId(): string | undefined {
  return _id;
}

/**
 * Get all feature flags
 * @returns Object with flag names and their values
 */
export function getFlags(): FeatureFlagsConfig {
  return Object.freeze(
    Object.entries(_flags).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as FeatureFlagsConfig)
  );
}

export function getFlagValue(flagName: string): FeatureFlagValue | undefined {
  return _flags[flagName];
}
